import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/shared/lib/cn";

/**
 * La tabla de datos del panel — LA tabla: cambiarla aquí cambia todas
 * las listas del sistema, que es exactamente lo que pasó el 27-ago con
 * el rediseño sobre la referencia "project-data-table":
 *
 *   · Las FILAS ENTRAN en cascada (opacidad + 16px desde abajo, 50ms
 *     entre fila y fila) — la firma visual de la referencia. Con
 *     `prefers-reduced-motion`, ninguna: la fila se pinta y ya.
 *   · El encabezado dejó las versalitas: texto normal, medio, en gris
 *     — más alto (h-12) y más callado, como la referencia.
 *   · El borde vive en la FILA y la última no lo lleva: la tabla
 *     termina donde termina el marco, sin una raya huérfana.
 *
 * Es una `<table>` DE VERDAD, no una rejilla de `<div>`: un lector de
 * pantalla anuncia "columna Estado, fila 3 de 24" solo si el marcado lo
 * dice, y `<th scope="col">` es lo que lo dice.
 *
 * `align` no usa el atributo HTML (obsoleto) sino clases, para que el
 * mismo valor alinee la celda y su cabecera.
 */
export type CellAlign = "left" | "center" | "right";

const alignClasses: Record<CellAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/*
 * ── El índice de cada fila, sin pedírselo a nadie ────────────────────
 * La cascada necesita saber "soy la fila N" y las páginas mapean filas
 * sin pasar índices. En vez de tocar cada tabla del sistema, `DataTable`
 * reparte turnos: un contador que se REINICIA en cada render del padre y
 * que cada `Tr` reclama UNA vez al montarse. Las filas ya montadas
 * conservan su turno (no re-animan al refrescar datos); las nuevas —un
 * cambio de página— empiezan de cero y entran en cascada otra vez.
 */
const RowIndexContext = createContext<() => number>(() => 0);

export interface DataTableFrameProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * El marco de la lista: una card BLANCA y limpia — el `rounded border
 * bg-card shadow` de la referencia (Anahí, 27-ago: "sigo viendo las
 * tablas igual" — el vidrio crema hacía invisible el rediseño). El
 * vidrio sigue siendo la superficie de los formularios y las barras;
 * los DATOS viven sobre blanco, donde las filas y sus bordes respiran.
 *
 * `overflow-hidden` se queda; un `<thead>` pegajoso no funcionaría aquí
 * dentro — el día que se quiera, el recorte sale de aquí.
 */
export function DataTableFrame({ className, children, ...rest }: DataTableFrameProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-3xl border border-ds-border bg-white shadow-ds-float",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface DataTableProps extends HTMLAttributes<HTMLTableElement> {
  /** Lo que anuncia la tabla a un lector de pantalla. */
  caption: string;
  children: ReactNode;
}

export function DataTable({ caption, className, children, ...rest }: DataTableProps) {
  const nextIndex = useRef(0);
  // Reinicio en CADA render del padre: los `Tr` que monten después de
  // este render reclaman turnos desde cero (ver el contexto, arriba).
  nextIndex.current = 0;
  const claimIndex = useCallback(() => nextIndex.current++, []);

  return (
    <div className="overflow-x-auto">
      <table
        className={cn(
          "w-full border-collapse text-sm",
          // El borde de las filas, con la última en silencio.
          "[&_tbody_tr]:border-b [&_tbody_tr]:border-ds-border [&_tbody_tr:last-child]:border-0",
          className,
        )}
        {...rest}
      >
        <caption className="sr-only">{caption}</caption>
        <RowIndexContext.Provider value={claimIndex}>{children}</RowIndexContext.Provider>
      </table>
    </div>
  );
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: CellAlign;
}

/**
 * La cabecera de columna, al estilo de la referencia: texto normal
 * (fuera versalitas), peso medio, gris `ds-ink-3` sobre el BLANCO de la
 * card — sin la banda crema de antes; el borde inferior es lo único que
 * la separa de las filas. Más aire (h-12).
 */
export function Th({ align = "left", className, children, ...rest }: ThProps) {
  return (
    <th
      scope="col"
      className={cn(
        "h-12 border-b border-ds-border px-4 align-middle text-[13px] font-medium text-ds-ink-3",
        alignClasses[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: CellAlign;
}

export function Td({ align = "left", className, children, ...rest }: TdProps) {
  return (
    <td
      className={cn("px-4 py-3 align-middle text-ds-ink", alignClasses[align], className)}
      {...rest}
    >
      {children}
    </td>
  );
}

export interface TrProps
  extends Omit<
    HTMLAttributes<HTMLTableRowElement>,
    // Los cuatro nombres que chocan con las props de animación de
    // framer-motion. Ninguna tabla del sistema los usa en un `<tr>`.
    "onAnimationStart" | "onDrag" | "onDragStart" | "onDragEnd"
  > {
  children: ReactNode;
}

/**
 * Una fila: entra en cascada (su turno lo reparte `DataTable`) y se
 * resalta al pasar por encima. El resalte `mist/60` está MEDIDO: la
 * fila resaltada conserva 14.11:1 con `ds-ink` — el texto ni se entera.
 *
 * La cascada se TOPA en la fila 12: en una página de 24, esperar
 * 1.2 segundos a la última fila ya no es una entrada, es una fila que
 * tarda.
 */
export function Tr({ className, children, ...rest }: TrProps) {
  const shouldReduceMotion = useReducedMotion();
  const claimIndex = useContext(RowIndexContext);
  // `useState` con inicializador: el turno se reclama UNA vez, al
  // montar, y sobrevive a los re-renders sin volver a animar.
  const [index] = useState(claimIndex);

  return (
    <motion.tr
      initial={shouldReduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: Math.min(index, 12) * 0.05,
        duration: 0.3,
        ease: "easeOut",
      }}
      className={cn(
        // Sobre blanco, el resalte baja a `mist/40`: la referencia usa
        // un hover callado, y el 60 sobre blanco gritaba.
        "transition-colors duration-200 hover:bg-ds-mist/40 motion-reduce:transition-none",
        className,
      )}
      {...rest}
    >
      {children}
    </motion.tr>
  );
}
